// Types
import type { TListingsStore } from '~/misc/listings/store/types';
import type { TEntityStatus } from '~/misc/opportunity/types';
import type { TListingFilters, TStoredFilters } from '~/misc/listings/types';
import type { TAutomatedEmailFilterGroup } from '~/modules/opportunities/automated-email/settings/types';
import { ESaleType } from '~/misc/listings/types';

// Config
import { DEFAULT_LISTINGS_FILTERS } from '~/misc/listings/filters/config';

// Store
import { useListingsStore } from '~/misc/listings/store';

// Models
import { PatchFiltersModel } from '~/modules/opportunities/automated-email/settings/models/patch-filters';

// Composables
import { useAutomatedEmailPatch } from '~/modules/opportunities/automated-email/composables/use-automated-email-patch';

// Utils
import { unwrapFilters } from '~/misc/listings/utils/filter-wrapper';
import { cleanStoredFilters } from '~/misc/listings/utils/filter-cleaner';
import { formatPatchFilterGroups } from '~/modules/opportunities/automated-email/settings/utils/format-patch-filter-groups';
import { buildPatchFiltersPayload } from '~/modules/opportunities/automated-email/settings/utils/build-patch-filters-payload';

export const usePatchFilters = () => {
  const model = new PatchFiltersModel();
  const listingsStore: TListingsStore = useListingsStore();
  const { error: showError } = useToast();
  const { patchId, saleType, isLettings } = useAutomatedEmailPatch();

  const filterGroups: Ref<TAutomatedEmailFilterGroup[]> = ref<TAutomatedEmailFilterGroup[]>([]);
  const filters: Ref<TListingFilters> = ref<TListingFilters>({ ...DEFAULT_LISTINGS_FILTERS });
  const isLoading: Ref<boolean> = ref<boolean>(false);
  const isSaving: Ref<boolean> = ref<boolean>(false);

  const setFilterGroups = (value: TAutomatedEmailFilterGroup[]) => {
    filterGroups.value = value;
  };

  const setFilters = (value: TListingFilters) => {
    filters.value = { ...value };
  };

  const setIsLoading = (value: boolean) => {
    isLoading.value = value;
  };

  const setIsSaving = (value: boolean) => {
    isSaving.value = value;
  };

  const buildEntityStatusNames = (): Record<number, string> =>
    listingsStore.entityStatuses.reduce(
      (acc: Record<number, string>, status: TEntityStatus) => {
        acc[status.id] = status.name;

        return acc;
      },
      {}
    );

  const rebuildGroups = (): void => {
    setFilterGroups(
      formatPatchFilterGroups(
        filters.value as unknown as Partial<TStoredFilters>,
        buildEntityStatusNames()
      )
    );
  };

  const applyFilters = (next: TListingFilters): void => {
    setFilters(next);
    rebuildGroups();
  };

  const savePatchFilters = async (next: TListingFilters): Promise<boolean> => {
    if (patchId.value === undefined) {
      return false;
    }

    setIsSaving(true);

    try {
      await model.savePatchFilters(patchId.value, saleType.value, buildPatchFiltersPayload(next));
      applyFilters(next);

      return true;
    } catch (error) {
      // eslint-disable-next-line no-console
      import.meta.dev && console.error('Failed to save patch filters:', error);
      showError({
        message: 'Failed to save filters. Please try again or login again if the error persists',
      });

      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const fetchFilters = async (): Promise<void> => {
    if (patchId.value === undefined) {
      setFilters({ ...DEFAULT_LISTINGS_FILTERS });
      setFilterGroups([]);

      return;
    }

    setIsLoading(true);

    try {
      const { filters: stored } = unwrapFilters(
        await model.getPatchFilters(patchId.value, saleType.value)
      );

      setFilters({
        ...DEFAULT_LISTINGS_FILTERS,
        ...cleanStoredFilters(stored),
        type: isLettings.value ? ESaleType.LETTINGS : ESaleType.SALES,
      });
      setFilterGroups(formatPatchFilterGroups(stored, buildEntityStatusNames()));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    filters,
    filterGroups,
    isLoading,
    isSaving,
    fetchFilters,
    applyFilters,
    savePatchFilters,
  };
};
